import React, { useContext, useMemo, useState } from 'react';
import PageLayout from '../pagelayout';
import { useNavigate, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import Button from '../components/button';
import translation from './activity.student.i18n';
import translationsProfile from '../practiceplaces/practiceplaceprofile/practiceplaceprofile.i18n';
import PanelHeader from '../components/panelheader/panelHeader';
import {
  dateWithLocale,
  fullDateWithLocale,
  shortDateWithLocale,
} from '../date.utils';
import * as css from './activity.student.module.scss';
import InfoIcon from '@mui/icons-material/Info';
import { useLoader } from '../api/useLoader';
import Spinner from '../components/spinner/spinner';
import {
  FagpersonDto,
  PraksisaktivitetDto,
  PraksisaktivitetStatuserDto,
  PraksisplassOnskeDto,
  PraksisstedprofilDto,
  StudentDto,
} from '../api/generated/student-generated-api';
import { StudentApiContext } from '../api/StudentApiContext';
import { Grid } from '@mui/material';
import { Language, LanguageContext } from '../i18n/LanguageStore';
import onskelisteTilgjengeligIcon from 'url:./images/onskelisteTilgjengelig.svg';
import { MobileFriendlyTable } from '../components/table';
import { DropResult } from 'react-beautiful-dnd';
import { HttpError } from '../api/generated/studiested-generated-api';
import Icon, { IconType } from '../components/icon';
import { EmptyState } from '../components/emptyState/emptyState';
import {
  AlpNotificationType,
  NotificationFunctionContext,
} from '../components/notifications/notificationProvider';
import { uuidv4 } from '../util/uuidv4';
import StandardTable from '../components/table';
import {
  Table,
  TableCellTh,
  TableCellTd,
  TableHeader,
  TableMain,
  TableRow,
} from '../components/table/alpTable';
import {
  mapHierarchyToHeadings,
  OnStedHoverCard,
  StedType,
} from '../components/hierarchy/onhoverhierarchyinfo';
import FritekstSectionReadOnly from './common/fritekstsectionreadonly';

const ActivityPage = () => {
  const { formatMessage } = useIntl();
  const { praksisApi } = useContext(StudentApiContext);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, loading } = useLoader(
    () =>
      id
        ? praksisApi.praksisaktivitetIdGet({
            pathParams: { id },
          })
        : Promise.reject('id er ikke definert'),
    [id],
  );

  const emneTitle = useMemo(
    () => (data ? `${data.emne.kode} - ${data.emne.navn}` : ''),
    [data],
  );

  const labels = useMemo(
    () => ({
      pageTitle: formatMessage({ id: translation.pageTitle.id }, { emneTitle }),
      onskelisteTitle: formatMessage(
        {
          id: translation.onskelisteTitle.id,
        },
        { emneTitle },
      ),
      back: formatMessage({ id: translation.back.id }),
      subtitle: formatMessage({ id: translation.subtitle.id }),
      prioriterOnskelisteHjelp: formatMessage({
        id: translation.prioriterOnskelisteHjelp.id,
      }),
      setUpWishList: formatMessage({ id: translation.setUpWishList.id }),
    }),
    [emneTitle, formatMessage],
  );

  const actionsButtons = (
    <>
      <Button.Tertiary onClick={() => navigate('/')}>
        {labels.back}
      </Button.Tertiary>
      {data?.status === PraksisaktivitetStatuserDto.OnskelisteTilgjengelig && (
        <Button.Primary
          onClick={() => navigate(`/activity/${id}/praksissteder`)}
        >
          {labels.setUpWishList}
        </Button.Primary>
      )}
    </>
  );

  const titleAccordingToStatus = useMemo(() => {
    switch (data?.status) {
      case PraksisaktivitetStatuserDto.OnskelisteTilgjengelig:
      case PraksisaktivitetStatuserDto.OnskelisteRegistrert:
        return labels.onskelisteTitle;
      default:
        return labels.pageTitle;
    }
  }, [data, labels]);

  return (
    <PageLayout
      isIndented={true}
      title={titleAccordingToStatus}
      subtitle={
        data?.status === PraksisaktivitetStatuserDto.OnskelisteRegistrert
          ? labels.prioriterOnskelisteHjelp
          : labels.subtitle
      }
      actionItems={actionsButtons}
    >
      {!data || loading ? <Spinner /> : <Activity praksisaktivitet={data} />}
    </PageLayout>
  );
};

interface CommonProps {
  praksisaktivitet: PraksisaktivitetDto;
}

const Activity: React.FC<React.PropsWithChildren<CommonProps>> = ({
  praksisaktivitet,
}) => {
  const { formatMessage, locale } = useIntl();
  const { selectedLanguage } = useContext(LanguageContext);

  const [activeFagpersonCol, setActiveFagpersonCol] = useState<number>(-1);
  const [fagpersonTableRows, setFagpersonTableRows] = useState<JSX.Element[]>();
  const [activeStudentCol, setActiveStudentCol] = useState<number>(-1);
  const [studentTableRows, setStudentTableRows] = useState<JSX.Element[]>();

  const {
    startDato: start,
    sluttDato: slutt,
    emne,
    status,
    onskelisteFrist,
    fritekst,
    fagpersoner,
    medstudenter,
    praksisstedprofil,
    onskeliste,
  } = praksisaktivitet;

  const startDato = new Date(start);
  const sluttDato = new Date(slutt);

  const labels = {
    praksisperiode: formatMessage({ id: translation.praksisperiode.id }),
    praksisplass: formatMessage({ id: translation.praksisplass.id }),
    emnebeskrivelse: formatMessage({
      id: translation.aktivitetsemnebeskrivelse.id,
    }),
    prioriterOnskelisteHjelp: formatMessage({
      id: translation.prioriterOnskelisteHjelp.id,
    }),
    setUpWishList: formatMessage({ id: translation.setUpWishList.id }),
    onskelisteFrist: formatMessage({ id: translation.onskelisteFrist.id }),
    fagpersoner: formatMessage({ id: translation.fagpersoner.id }),
    medstudenter: formatMessage({ id: translation.medstudenter.id }),
    fritekstTitle: formatMessage({ id: translation.fritekstTitle.id }),
    fritekstSubtitle: formatMessage({ id: translation.fritekstSubtitle.id }),
  };

  const fagpersonLabels = [
    { title: formatMessage({ id: translation.kontakt.id }) },
    { title: formatMessage({ id: translation.rolle.id }) },
    { title: formatMessage({ id: translation.epost.id }) },
    { title: formatMessage({ id: translation.telefon.id }) },
  ];

  const translateStatus = (status: string) => {
    return formatMessage({ id: translation[`status${status}`].id });
  };

  const getFagpersonRows = (sortedFagepersoner?: FagpersonDto[]) => {
    const headers = fagpersonLabels;

    return (sortedFagepersoner || fagpersoner).map((fagperson, index) => {
      let tabHeader = 0;

      return (
        <TableRow key={`fagperson-row-${index}`}>
          <TableCellTd header={headers[tabHeader++].title}>
            {`${fagperson.fornavn} ${fagperson.etternavn}`}
          </TableCellTd>
          <TableCellTd header={headers[tabHeader++].title}>
            {translateStatus(fagperson.rolle!)}
          </TableCellTd>
          <TableCellTd header={headers[tabHeader++].title}>
            {(fagperson.epost && (
              <a href={'mailto:' + fagperson.epost}>{fagperson.epost}</a>
            )) ||
              ''}
          </TableCellTd>
          <TableCellTd header={headers[tabHeader].title}>
            {(fagperson.telefonnummer && (
              <a href={'tel:' + fagperson.telefonnummer}>
                {fagperson.telefonnummer}
              </a>
            )) ||
              ''}
          </TableCellTd>
        </TableRow>
      );
    });
  };

  const compare = Intl.Collator(locale || 'nb').compare;
  const sortFagpersoner = (col: number, a: FagpersonDto, b: FagpersonDto) => {
    switch (col) {
      case 0:
        return compare(a.etternavn, b.etternavn);
      case 1:
        return compare(translateStatus(a.rolle), translateStatus(b.rolle));
      case 2:
        return compare(a.epost || '0', b.epost || '0');
    }
    return 0;
  };

  const sortFagpersonerByActiveCol = (col: number, ascending: boolean) => {
    setFagpersonTableRows(
      getFagpersonRows(
        fagpersoner.sort((a, b) =>
          ascending ? sortFagpersoner(col, b, a) : sortFagpersoner(col, a, b),
        ),
      ),
    );
  };

  const canSortIndices = [0, 1, 2];

  const getFagpersonHeaders = () =>
    fagpersonLabels.map(({ title }, index) => (
      <TableCellTh
        key={title}
        colIndex={index}
        activeCol={index === activeFagpersonCol}
        setActiveCol={setActiveFagpersonCol}
        sort={sortFagpersonerByActiveCol}
        canSort={canSortIndices.includes(index)}
      >
        {title}
      </TableCellTh>
    ));

  const studentLabels = [
    { title: formatMessage({ id: translation.kontakt.id }) },
    { title: formatMessage({ id: translation.epost.id }) },
  ];

  const getStudentRows = (sortedStudents?: StudentDto[]) => {
    const headers = studentLabels;

    return (sortedStudents || medstudenter).map((student, index) => {
      let tabHeader = 0;

      return (
        <TableRow key={`student-row-${index}`}>
          <TableCellTd header={headers[tabHeader++].title}>
            {`${student.fornavn} ${student.etternavn}`}
          </TableCellTd>
          <TableCellTd header={headers[tabHeader].title}>
            {(student.epost && (
              <a href={'mailto:' + student.epost}>{student.epost}</a>
            )) ||
              ''}
          </TableCellTd>
        </TableRow>
      );
    });
  };

  const sortStudents = (col: number, a: StudentDto, b: StudentDto) => {
    switch (col) {
      case 0:
        return compare(a.etternavn, b.etternavn);
      case 1:
        return compare(a.epost || '0', b.epost || '0');
    }
    return 0;
  };

  const sortStudentsByActiveCol = (col: number, ascending: boolean) => {
    setStudentTableRows(
      getStudentRows(
        medstudenter.sort((a, b) =>
          ascending ? sortStudents(col, b, a) : sortStudents(col, a, b),
        ),
      ),
    );
  };

  const getStudentHeaders = () =>
    studentLabels.map(({ title }, index) => (
      <TableCellTh
        key={title}
        colIndex={index}
        activeCol={index === activeStudentCol}
        setActiveCol={setActiveStudentCol}
        sort={sortStudentsByActiveCol}
        canSort
      >
        {title}
      </TableCellTh>
    ));

  const assignedPraksisplass = praksisstedprofil && (
    <>
      <PanelHeader title={labels.praksisplass} />
      <PraksisstedProfil profile={praksisstedprofil} />
      {fritekst && (
        <FritekstSectionReadOnly
          fritekst={fritekst}
          title={labels.fritekstTitle}
          subtitle={labels.fritekstSubtitle}
        />
      )}
      {fagpersoner.length > 0 && (
        <>
          <PanelHeader title={labels.fagpersoner} />
          <Table>
            <TableHeader>
              <TableRow top>{getFagpersonHeaders()}</TableRow>
            </TableHeader>
            <TableMain>{fagpersonTableRows || getFagpersonRows()}</TableMain>
          </Table>
        </>
      )}

      {medstudenter.length > 0 && (
        <>
          <PanelHeader title={labels.medstudenter} />
          <Table>
            <TableHeader>
              <TableRow top>{getStudentHeaders()}</TableRow>
            </TableHeader>
            <TableMain>{studentTableRows || getStudentRows()}</TableMain>
          </Table>
        </>
      )}
      {onskeliste.length > 0 && (
        <OnskelisteImmutable
          praksisaktivitet={praksisaktivitet}
          selectedLanguage={selectedLanguage}
        />
      )}
    </>
  );

  const emptyStateLabels = {
    emptyStateTitle: formatMessage({ id: translation.emptyStateTitle.id }),
    emptyStateText: formatMessage({ id: translation.emptyStateText.id }),
  };

  return (
    <>
      <div className={css.page}>
        <div className="status">
          <PanelHeader
            title={translateStatus(status!)}
            color={'#ffffff'}
            backgroundColor={'#445566'}
            padLeft
          />
        </div>
        <Grid container className={css.generalInfo}>
          <Grid item xs={12} sm={5} className={css.praksisPeriod}>
            <p>
              <span className={css.label}>{labels.praksisperiode}:</span>
              <span className={css.dateRange}>
                {dateWithLocale(startDato, selectedLanguage)} -{' '}
                {dateWithLocale(sluttDato, selectedLanguage)}
              </span>
            </p>
          </Grid>
          {status === PraksisaktivitetStatuserDto.OnskelisteTilgjengelig && (
            <Grid item xs={12} sm={3} className={css.onskelisteFrist}>
              <p className={css.label}>{labels.onskelisteFrist}</p>
              <p className={css.date}>
                {dateWithLocale(new Date(onskelisteFrist!), selectedLanguage)}
              </p>
            </Grid>
          )}
          <Grid
            item
            xs={12}
            sm={
              status === PraksisaktivitetStatuserDto.OnskelisteTilgjengelig
                ? 4
                : 7
            }
            className={css.emnebeskrivelse}
          >
            <div className={css.emne}>
              <InfoIcon fontSize="large" />{' '}
              <span className={css.label}>{labels.emnebeskrivelse}</span>:{' '}
              <span className="emnekode">{emne.kode}</span>
            </div>
          </Grid>
        </Grid>
        {(status === PraksisaktivitetStatuserDto.Registrert ||
          status === PraksisaktivitetStatuserDto.IkkePublisert) && (
          <>
            <PanelHeader title={labels.praksisplass} />
            <EmptyState
              title={emptyStateLabels.emptyStateTitle}
              text={emptyStateLabels.emptyStateText}
            />
            {onskeliste.length > 0 &&
              status === PraksisaktivitetStatuserDto.IkkePublisert && (
                <OnskelisteImmutable
                  praksisaktivitet={praksisaktivitet}
                  selectedLanguage={selectedLanguage}
                />
              )}
            {fritekst && (
              <FritekstSectionReadOnly
                fritekst={fritekst}
                title={labels.fritekstTitle}
                subtitle={labels.fritekstSubtitle}
              />
            )}
          </>
        )}
        {status === PraksisaktivitetStatuserDto.OnskelisteTilgjengelig && (
          <>
            <PanelHeader title={labels.praksisplass} />
            <OnskelisteTilgjengelig
              praksisaktivitet={praksisaktivitet}
              selectedLanguage={selectedLanguage}
            />
            {fritekst && (
              <FritekstSectionReadOnly
                fritekst={fritekst}
                title={labels.fritekstTitle}
                subtitle={labels.fritekstSubtitle}
              />
            )}
          </>
        )}
        {(status === PraksisaktivitetStatuserDto.PraksisplassMottatt ||
          status === PraksisaktivitetStatuserDto.Aktiv ||
          status === PraksisaktivitetStatuserDto.Gjennomfoert) &&
          assignedPraksisplass}
        {status === PraksisaktivitetStatuserDto.OnskelisteRegistrert && (
          <>
            <OnskelisteRegistrert
              praksisaktivitet={praksisaktivitet}
              selectedLanguage={selectedLanguage}
            />
            {fritekst && (
              <FritekstSectionReadOnly
                fritekst={fritekst}
                title={labels.fritekstTitle}
                subtitle={labels.fritekstSubtitle}
              />
            )}
          </>
        )}
        {status ===
          PraksisaktivitetStatuserDto.OnskelisteRegistrertIkkeTilgjengelig && (
          <>
            <PanelHeader title={labels.praksisplass} />
            <EmptyState
              title={emptyStateLabels.emptyStateTitle}
              text={emptyStateLabels.emptyStateText}
            />
            <OnskelisteImmutable
              praksisaktivitet={praksisaktivitet}
              selectedLanguage={selectedLanguage}
            />
            {fritekst && (
              <FritekstSectionReadOnly
                fritekst={fritekst}
                title={labels.fritekstTitle}
                subtitle={labels.fritekstSubtitle}
              />
            )}
          </>
        )}
      </div>
    </>
  );
};

interface OnskelisteProps extends CommonProps {
  selectedLanguage: Language;
}

const OnskelisteTilgjengelig: React.FC<
  React.PropsWithChildren<OnskelisteProps>
> = ({ praksisaktivitet, selectedLanguage }) => {
  const { formatMessage } = useIntl();
  const { id, onskelisteFrist } = praksisaktivitet;
  const navigate = useNavigate();

  const labels = {
    onskelisteDeadlineHeader: formatMessage(
      { id: translation.onskelisteDeadlineHeader.id },
      {
        deadline: shortDateWithLocale(
          new Date(onskelisteFrist!),
          selectedLanguage,
        ),
      },
    ),
    onskelisteTilgjengeligInfo1: formatMessage({
      id: translation.onskelisteTilgjengeligInfo1.id,
    }),
    onskelisteTilgjengeligInfo2: formatMessage({
      id: translation.onskelisteTilgjengeligInfo2.id,
    }),
    onskelisteTilgjengeligInfo3: formatMessage({
      id: translation.onskelisteTilgjengeligInfo3.id,
    }),
    setUpWishList: formatMessage({ id: translation.setUpWishList.id }),
  };

  return (
    <div className={css.praksisplassArea}>
      <div className={css.imgWrapper}>
        <img src={onskelisteTilgjengeligIcon} alt="" />
      </div>
      <h3 className={css.heading}>{labels.onskelisteDeadlineHeader}</h3>
      <p>{labels.onskelisteTilgjengeligInfo1}</p>
      <p>{labels.onskelisteTilgjengeligInfo2}</p>
      <p>{labels.onskelisteTilgjengeligInfo3}</p>
      <Button.Secondary
        onClick={() => navigate(`/activity/${id}/praksissteder`)}
        className="setUpWishListButton"
      >
        {labels.setUpWishList}
      </Button.Secondary>
    </div>
  );
};

const PraksisstedProfil: React.FC<
  React.PropsWithChildren<{ profile: PraksisstedprofilDto }>
> = ({ profile }) => {
  const { formatMessage } = useIntl();
  const labels = {
    title: formatMessage(
      { id: translationsProfile.title.id },
      { practicePlaceName: profile.navn },
    ),
    subtitle: formatMessage({ id: translationsProfile.subtitle.id }),
    profileImgAltText: formatMessage({
      id: translationsProfile.profileImgAltText.id,
    }),
    orgnr: formatMessage({ id: translationsProfile.orgnr.id }),
    goto: formatMessage({ id: translationsProfile.goto.id }),
    adresse: formatMessage({ id: translationsProfile.adresse.id }),
    kontaktinfo: formatMessage({ id: translationsProfile.kontaktinfo.id }),
    epost: formatMessage({ id: translationsProfile.epost.id }),
    telefon: formatMessage({ id: translationsProfile.telefon.id }),
    nettside: formatMessage({ id: translationsProfile.nettside.id }),
    beskrivelse: formatMessage({ id: translationsProfile.beskrivelse.id }),
    krav: formatMessage({ id: translationsProfile.krav.id }),
  };

  const hierarchy = (profile.noder?.over.slice(0).reverse() || []).concat({
    id: profile.id,
    navn: profile.navn,
  });

  return (
    <div>
      <Grid container alignItems={'stretch'}>
        <Grid item xs={12} md={7}>
          {profile.bildeUrl && (
            <div className={css.profilePicture} aria-hidden="true">
              <img src={profile.bildeUrl} alt={labels.profileImgAltText} />
            </div>
          )}
          {!profile.bildeUrl && (
            <div className={css.profilePictureReplacement} aria-hidden="true">
              <div className={css.iconWrapper}>
                <Icon type={IconType.EndNode} title={''} />
              </div>
            </div>
          )}
        </Grid>
        <Grid item md={1} />
        <Grid item className={css.praksisstedWrapper} xs={12} md={4}>
          <div className={css.omPraksissted}>
            <p>
              <strong>{profile.navn}</strong>
            </p>
            {hierarchy.length > 1 && (
              <div className={css.headings}>
                {mapHierarchyToHeadings(hierarchy)}
              </div>
            )}
          </div>

          {profile.adresse && (
            <p className={css.adresse}>
              <span className={css.srOnly}>{labels.adresse}:</span>
              {profile.adresse.adresselinje1 && (
                <>
                  {profile.adresse.adresselinje1}
                  <br />
                </>
              )}
              {profile.adresse.adresselinje2 && (
                <>
                  {profile.adresse.adresselinje2}
                  <br />
                </>
              )}
              {profile.adresse.postnr} {profile.adresse.poststed}
            </p>
          )}

          <div className={css.kommunikasjon}>
            {profile.nettsideUrl && (
              <p>
                <span>{labels.nettside}:</span>{' '}
                <a
                  href={
                    profile.nettsideUrl.match(/^https?:\/\//)
                      ? profile.nettsideUrl
                      : `https://${profile.nettsideUrl}`
                  }
                >
                  {`${labels.goto} ${profile.navn}`}
                </a>
              </p>
            )}
            {profile.epost && (
              <p>
                <span>{labels.epost}:</span>{' '}
                <a href={`mailto: ${profile.epost}`}>{profile.epost}</a>
              </p>
            )}
            {profile.telefonnummer && (
              <p>
                <span>{labels.telefon}:</span>{' '}
                <span>{profile.telefonnummer}</span>
              </p>
            )}
          </div>
        </Grid>
        {profile.beskrivelse && (
          <>
            <Grid item xs={12} md={7}>
              <PanelHeader title={labels.beskrivelse} />
              <p>{profile.beskrivelse}</p>
            </Grid>
          </>
        )}
        <Grid item md={5} />
        {profile.krav && (
          <>
            <Grid item xs={12} md={7}>
              <PanelHeader title={labels.krav} />
              <p>{profile.krav}</p>
            </Grid>
            <Grid item md={5} />
          </>
        )}
      </Grid>
    </div>
  );
};

const OnskelisteRegistrert: React.FC<
  React.PropsWithChildren<OnskelisteProps>
> = ({ praksisaktivitet, selectedLanguage }) => {
  const { formatMessage } = useIntl();
  const { praksisApi } = useContext(StudentApiContext);
  const { addNotification } = useContext(NotificationFunctionContext);
  const navigate = useNavigate();

  const [onskeliste, setOnskeliste] = useState(praksisaktivitet.onskeliste);

  const { id: aktivitetId } = praksisaktivitet;

  const labels = {
    ranking: formatMessage({ id: translation.ranking.id }),
    praksisplass: formatMessage({ id: translation.praksisplass.id }),
    workPlacementTop: formatMessage({ id: translation.praksisstedTop.id }),
    workPlacementLeaf: formatMessage({ id: translation.praksisstedLeaf.id }),
    action: formatMessage({ id: translation.action.id }),
    wishListOpenUntil: formatMessage(
      { id: translation.wishListOpenUntil.id },
      {
        deadline: shortDateWithLocale(
          new Date(praksisaktivitet.onskelisteFrist!),
          selectedLanguage,
        ),
      },
    ),
    changeWishList: formatMessage({ id: translation.changeWishList.id }),
    removeOnske: formatMessage({ id: translation.removeOnske.id }),
    onskeSlettet: formatMessage({ id: translation.onskeSlettet.id }),
    onskeFlyttet: formatMessage({ id: translation.onskeFlyttet.id }),
    wishList: formatMessage({ id: translation.wishList.id }),
    prioriterOnskelisteHjelp: formatMessage({
      id: translation.prioriterOnskelisteHjelp.id,
    }),
  };

  const reorder = (
    list: PraksisplassOnskeDto[],
    startIndex: number,
    endIndex: number,
  ) => {
    const onske = list[startIndex];
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    setOnskeliste([...result]);

    praksisApi
      .praksisaktivitetIdOnskeOnskeIdPut({
        pathParams: {
          id: aktivitetId,
          onskeId: onske.id || '',
        },
        body: endIndex,
      })
      .then(() => {
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.SUCCESS,
          message: labels.onskeFlyttet,
        });
      })
      .catch((err: HttpError) => {
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.ERROR,
          message: err.body,
        });
        setOnskeliste([...list]);
      });
  };

  const onDragEnd = (result: DropResult) => {
    if (
      !result.destination ||
      result.destination.index === result.source.index
    ) {
      return;
    }
    reorder(onskeliste, result.source.index, result.destination.index);
  };

  const removeOnske = (onske: PraksisplassOnskeDto) => {
    praksisApi
      .praksisaktivitetIdOnskeOnskeIdDelete({
        pathParams: {
          id: aktivitetId,
          onskeId: onske.id || '',
        },
      })
      .then(() => {
        onskeliste.splice(onskeliste.indexOf(onske), 1);
        setOnskeliste([...onskeliste]);

        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.SUCCESS,
          message: labels.onskeSlettet,
        });
      })
      .catch((err: HttpError) =>
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.ERROR,
          message: err.body,
        }),
      );
  };

  const columns = [
    {
      title: labels.ranking,
      data: (onske: PraksisplassOnskeDto, rowIndex: number) =>
        `${rowIndex + 1}`,
    },
    {
      title: labels.workPlacementTop,
      data: (onske: PraksisplassOnskeDto) => onske.praksissted.navn,
    },
    {
      title: labels.workPlacementLeaf,
      data: (onske: PraksisplassOnskeDto) => (
        <div className={css.practiceplaceName}>
          {onske.praksisplass.navn}
          <OnStedHoverCard
            id={onske.praksisplass.id}
            navn={onske.praksisplass.navn}
            sted={StedType.PRAKSISSTED}
          />
        </div>
      ),
    },
    {
      title: labels.action,
      data: (onske: PraksisplassOnskeDto) => (
        <Button.Quaternary onClick={() => removeOnske(onske)}>
          {labels.removeOnske}
        </Button.Quaternary>
      ),
    },
  ];

  return (
    <>
      <MobileFriendlyTable
        caption={<PanelHeader title={labels.wishList} />}
        dragAndDropTableProps={{ onDragEnd, dragAndDropId: 'wishList' }}
        columns={columns}
        rows={onskeliste}
        rowKey={(onske: PraksisplassOnskeDto) => onske.id || ''}
      />
      <Grid container className={css.changeWishListRow}>
        <Grid item xs={12} sm={4}>
          <p>{labels.wishListOpenUntil}</p>
        </Grid>
        <Grid item xs={12} sm={4}>
          <p>{labels.prioriterOnskelisteHjelp}</p>
        </Grid>
        <Grid item xs={12} sm={4} className={css.changeWishListButtonWrapper}>
          <Button.Secondary
            onClick={() => navigate(`/activity/${aktivitetId}/praksissteder`)}
            className="changeWishListButton"
          >
            {labels.changeWishList}
          </Button.Secondary>
        </Grid>
      </Grid>
    </>
  );
};

const OnskelisteImmutable: React.FC<
  React.PropsWithChildren<OnskelisteProps>
> = ({ praksisaktivitet, selectedLanguage }) => {
  const { formatMessage } = useIntl();

  const labels = {
    ranking: formatMessage({ id: translation.ranking.id }),
    praksisplass: formatMessage({ id: translation.praksisplass.id }),
    workPlacementTop: formatMessage({ id: translation.praksisstedTop.id }),
    workPlacementLeaf: formatMessage({ id: translation.praksisstedLeaf.id }),
    wishListWasOpenUntil: formatMessage(
      { id: translation.wishListWasOpenUntil.id },
      {
        deadline: fullDateWithLocale(
          new Date(praksisaktivitet.onskelisteFrist!),
          selectedLanguage,
        ),
      },
    ),
    wishList: formatMessage({ id: translation.wishList.id }),
    wishListClosed: formatMessage({ id: translation.wishListClosed.id }),
  };

  const headers = [
    { title: labels.ranking },
    { title: labels.workPlacementTop },
    { title: labels.workPlacementLeaf },
  ];

  const getRows = () =>
    praksisaktivitet.onskeliste.map((onske, rowIndex) => {
      let tabHeader = 0;

      return (
        <TableRow key={rowIndex}>
          <TableCellTd header={headers[tabHeader++].title}>{`${
            rowIndex + 1
          }`}</TableCellTd>
          <TableCellTd header={headers[tabHeader++].title}>
            {onske.praksissted.navn}
          </TableCellTd>
          <TableCellTd header={headers[tabHeader].title}>
            <div className={css.practiceplaceName}>
              {onske.praksisplass.navn}
              <OnStedHoverCard
                id={onske.praksisplass.id}
                navn={onske.praksisplass.navn}
                sted={StedType.PRAKSISSTED}
              />
            </div>
          </TableCellTd>
        </TableRow>
      );
    });

  const { onskelisteFrist } = praksisaktivitet;

  const getOnskeListeLabel = () => {
    if (onskelisteFrist && new Date() < new Date(onskelisteFrist)) {
      return labels.wishListClosed;
    }
    return labels.wishListWasOpenUntil;
  };

  return (
    <>
      <PanelHeader title={labels.wishList} />
      <StandardTable headers={headers} rows={getRows()} />
      <Grid container className={css.changeWishListRow}>
        <Grid item xs={12} sm={6}>
          <p>{getOnskeListeLabel()}</p>
        </Grid>
      </Grid>
    </>
  );
};

export default ActivityPage;
